import { Box3, BufferAttribute, BufferGeometry, Matrix4, Vector3 } from 'three'
import { GLB_INSTANCE_OWNED_GEOMETRY } from './disposeGlbClone'

const CAD_UV_SPAN = 2
const box = new Box3()
const size = new Vector3()
const worldDirA = new Vector3()
const worldDirB = new Vector3()
const worldUp = new Vector3(0, 1, 0)

type Axis = 'x' | 'y' | 'z'

function uvSpanIsCadRange(geometry: BufferGeometry): boolean {
  const uv = geometry.attributes.uv
  if (!uv || uv.count === 0) return true
  let minU = Infinity
  let minV = Infinity
  let maxU = -Infinity
  let maxV = -Infinity
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i)
    const v = uv.getY(i)
    if (u < minU) minU = u
    if (v < minV) minV = v
    if (u > maxU) maxU = u
    if (v > maxV) maxV = v
  }
  return maxU - minU > CAD_UV_SPAN || maxV - minV > CAD_UV_SPAN
}

function axisCoord(axis: Axis, x: number, y: number, z: number): number {
  if (axis === 'x') return x
  if (axis === 'y') return y
  return z
}

function localAxisWorldDir(
  matrixWorld: Matrix4 | undefined,
  axis: Axis,
  target: Vector3,
): Vector3 {
  if (!matrixWorld) {
    if (axis === 'x') return target.set(1, 0, 0)
    if (axis === 'y') return target.set(0, 1, 0)
    return target.set(0, 0, 1)
  }
  const e = matrixWorld.elements
  if (axis === 'x') return target.set(e[0], e[1], e[2]).normalize()
  if (axis === 'y') return target.set(e[4], e[5], e[6]).normalize()
  return target.set(e[8], e[9], e[10]).normalize()
}

/**
 * three@0.160 BufferGeometry.copy() assigns `userData` by reference.
 * Flags written on a clone leak to the catalog cache and every other instance.
 */
export function isolateGeometryUserData(geometry: BufferGeometry): BufferGeometry {
  geometry.userData = { ...geometry.userData }
  return geometry
}

function writeFaceNormalUvs(
  owned: BufferGeometry,
  sx: number,
  sy: number,
  sz: number,
): void {
  const pos = owned.attributes.position
  const nrm = owned.attributes.normal
  const uvs = new Float32Array(pos.count * 2)

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    const nx = Math.abs(nrm?.getX(i) ?? 0)
    const ny = Math.abs(nrm?.getY(i) ?? 0)
    const nz = Math.abs(nrm?.getZ(i) ?? 0)
    let u = 0
    let v = 0
    if (nx >= ny && nx >= nz) {
      u = (z - box.min.z) / sz
      v = (y - box.min.y) / sy
    } else if (ny >= nx && ny >= nz) {
      u = (x - box.min.x) / sx
      v = (z - box.min.z) / sz
    } else {
      u = (x - box.min.x) / sx
      v = (y - box.min.y) / sy
    }
    uvs[i * 2] = u
    uvs[i * 2 + 1] = v
  }

  owned.setAttribute('uv', new BufferAttribute(uvs, 2))
}

function writePanelUvs(
  mesh: {
    matrixWorld?: Matrix4
    updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void
  },
  owned: BufferGeometry,
  sx: number,
  sy: number,
  sz: number,
): void {
  const axes: { axis: Axis; size: number; min: number }[] = [
    { axis: 'x', size: sx, min: box.min.x },
    { axis: 'y', size: sy, min: box.min.y },
    { axis: 'z', size: sz, min: box.min.z },
  ].sort((a, b) => b.size - a.size)
  const faceA = axes[0]
  const faceB = axes[1]
  const yFace = axes.find((entry) => entry.axis === 'y' && (entry === faceA || entry === faceB))
  let vAxis = yFace ?? faceA
  let uAxis = vAxis === faceA ? faceB : faceA
  if (!yFace) {
    mesh.updateWorldMatrix?.(true, false)
    localAxisWorldDir(mesh.matrixWorld, faceA.axis, worldDirA)
    localAxisWorldDir(mesh.matrixWorld, faceB.axis, worldDirB)
    vAxis =
      Math.abs(worldDirA.dot(worldUp)) >= Math.abs(worldDirB.dot(worldUp)) ? faceA : faceB
    uAxis = vAxis === faceA ? faceB : faceA
  }

  const pos = owned.attributes.position
  const uvs = new Float32Array(pos.count * 2)
  const uSize = uAxis.size || 1
  const vSize = vAxis.size || 1

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    uvs[i * 2] = (axisCoord(uAxis.axis, x, y, z) - uAxis.min) / uSize
    uvs[i * 2 + 1] = (axisCoord(vAxis.axis, x, y, z) - vAxis.min) / vSize
  }

  owned.setAttribute('uv', new BufferAttribute(uvs, 2))
}

/**
 * CAD/STEP handles (e.g. 55532012835) keep logo UVs in inches (−8..19).
 * Repeating a catalog swatch on that range looks like a gold thread.
 * Furniture GLBs stay on authored 0..1 UVs unless `force` (2-door kits share one
 * atlas: each leaf is 0–0.5 / 0.5–1 and the swatch looks cut down the middle).
 * Door/costado panels with CAD-range UVs project on the two large axes (V up)
 * so the thin edge does not steal the unwrap.
 */
export function applyCatalogSwatchUvsIfNeeded(
  mesh: {
    geometry?: BufferGeometry
    matrixWorld?: Matrix4
    updateWorldMatrix?: (updateParents: boolean, updateChildren: boolean) => void
  },
  options?: { panel?: boolean; force?: boolean },
): boolean {
  const geometry = mesh.geometry
  if (!geometry?.attributes?.position) return false
  const alreadyRemapped =
    Boolean(geometry.userData.__catalogSwatchUvs) && !uvSpanIsCadRange(geometry)
  if (alreadyRemapped) return true
  if (!options?.force && !uvSpanIsCadRange(geometry)) return false

  if (
    !geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] ||
    geometry.userData.__catalogSwatchUvs
  ) {
    mesh.geometry = isolateGeometryUserData(geometry.clone())
    mesh.geometry.userData[GLB_INSTANCE_OWNED_GEOMETRY] = true
  } else {
    isolateGeometryUserData(geometry)
  }
  const owned = mesh.geometry
  owned.computeBoundingBox()
  if (!owned.boundingBox) return false
  box.copy(owned.boundingBox)
  box.getSize(size)

  const sx = size.x || 1
  const sy = size.y || 1
  const sz = size.z || 1

  if (options?.panel) {
    writePanelUvs(mesh, owned, sx, sy, sz)
  } else {
    writeFaceNormalUvs(owned, sx, sy, sz)
  }

  owned.userData.__catalogSwatchUvs = true
  return true
}

export function zoneNamesMatch(a?: string, b?: string): boolean {
  if (!a || !b) return false
  if (a === b) return true
  const normalize = (value: string) => value.replace(/[\s._<>]/g, '').toLowerCase()
  return normalize(a) === normalize(b)
}
